import React from 'react'
import { FormattedMessage } from 'react-intl'
import { css, cx } from '@emotion/css'
import clipboardCopy from 'clipboard-copy'
import { OrganizationProps, SampleApiRequest } from './sample-api-request'
import { Dropdown } from '../../legacy-design-system/dropdown'
import { Field } from './field'
import { Role, RoleSwitch } from '../roles'
import { getAgentKeysRoute } from '../route'
import {
  formSectionTitleCss,
  metadataCss
} from '../../legacy-design-system/text'
import { Link, Text } from '@contrast-security-inc/design-system-typography'

const linkCss = css`
  font-size: 12px;
`

const keysContainerCss = css`
  display: flex;
  flex-direction: column;
`
const infoContainerCss = css`
  display: flex;
  flex-direction: row;
`
const orgKeysContainerCss = cx(
  keysContainerCss,
  css`
    margin-right: 100px;
  `
)
const itemSpaceCss = css`
  margin-bottom: 20px;
`
const footerCss = css`
  display: flex;
  justify-content: flex-end;
`

const dropdownCss = css`
  width: auto;
`

interface YourKeysParentProps {
  readonly apiKey: string
  readonly contrastURL: string
  readonly serviceKey: string
  readonly authHeader: string
  readonly onRotateServiceKey: () => void
  readonly onCopyAuthHeader: () => void
  readonly onCopyAPIRequest: () => void
  readonly renderOrganizationDropdown?: () => React.ReactNode
}

interface YourKeysProps extends YourKeysParentProps {
  readonly role: Role
  readonly organization: OrganizationProps
  readonly renderOrganizationDropdown?: () => React.ReactNode
}

// Extract the link component to address SonarCloud issue
interface ExternalLinkProps {
  readonly isEmbeddedInNS: boolean
  readonly children: React.ReactNode
}

// Export the component for testing
export const ExternalAgentLink: React.FC<ExternalLinkProps> = ({
  isEmbeddedInNS,
  children
}) => {
  if (isEmbeddedInNS) {
    return <>{children}</>
  }
  return (
    <Link href={getAgentKeysRoute()} isExternal>
      {children}
    </Link>
  )
}

export class YourKeys extends React.Component<YourKeysProps> {
  public render() {
    const isEmbeddedInNS = window.isEmbeddedInNS === true
    const {
      role,
      organization,
      apiKey,
      contrastURL,
      serviceKey,
      authHeader,
      onRotateServiceKey,
      onCopyAuthHeader,
      onCopyAPIRequest,
      renderOrganizationDropdown
    } = this.props

    return (
      <div>
        <div className={infoContainerCss}>
          <div className={orgKeysContainerCss}>
            <div
              className={cx(
                itemSpaceCss,
                metadataCss,
                css`
                  font-size: 16px;
                `
              )}
            >
              <FormattedMessage
                id={'detail_apiKeys_useApiKeys'}
                values={{
                  a: (msg) => (
                    <Link href="/Contrast/docs/restapi/index.html" isExternal>
                      {msg}
                    </Link>
                  )
                }}
              />
            </div>

            <FormattedMessage id="title_apiKeysYour">
              {(text) => (
                <div className={cx(itemSpaceCss, formSectionTitleCss)}>
                  {text}
                </div>
              )}
            </FormattedMessage>

            {renderOrganizationDropdown && renderOrganizationDropdown()}

            <Field
              className={itemSpaceCss}
              contrastScrapeID="contrast-organization-uuid"
              label={{
                id: 'title_organizationUUID'
              }}
              value={organization.id}
              testId="organization-uuid"
            />

            <Field
              className={itemSpaceCss}
              contrastScrapeID="contrast-api-key"
              label={{
                id: 'title_apiKeyYour'
              }}
              value={apiKey}
              testId="api-key"
            />

            <Field
              contrastScrapeID="contrast-service-key"
              label={{
                id: 'title_serviceKey'
              }}
              value={serviceKey}
              inlineDescription={true}
              description={
                <Text font="metadata" color="neutral60">
                  <FormattedMessage id="action_rotateKeys">
                    {(text) => (
                      <Link className={linkCss} onClick={onRotateServiceKey}>
                        {text}
                      </Link>
                    )}
                  </FormattedMessage>
                </Text>
              }
              testId="service-key"
            />
            <div className={css(metadataCss, itemSpaceCss)}>
              <RoleSwitch
                role={role}
                requiredRole="ADMIN"
                renderAllowed={() => (
                  <Text font="metadata" color="neutral60">
                    <FormattedMessage
                      id={
                        organization.isSuperadmin
                          ? 'detail_learnHowToUseServiceWithSysLink'
                          : 'detail_learnHowToUseServiceWithLink'
                      }
                      values={{
                        b: (...msg) => <b>{msg}</b>,
                        a: (...msg) => (
                          <ExternalAgentLink isEmbeddedInNS={isEmbeddedInNS}>
                            {msg}
                          </ExternalAgentLink>
                        )
                      }}
                    />
                  </Text>
                )}
              />
            </div>

            <Field
              className={itemSpaceCss}
              label={{
                id: 'title_profile_authorizationHeader'
              }}
              description={
                <FormattedMessage id="action_copy">
                  {(text) => (
                    <Link
                      className={linkCss}
                      onClick={() =>
                        this.copyToClipboard(authHeader, onCopyAuthHeader)
                      }
                    >
                      {text}
                    </Link>
                  )}
                </FormattedMessage>
              }
              testId="authorization-header"
            />
            <RoleSwitch
              role={role}
              requiredRole="ADMIN"
              renderAllowed={() => (
                <div>
                  <FormattedMessage
                    id={
                      organization.isSuperadmin
                        ? 'detail_apiKeys_navigateToSysAgentKeys'
                        : 'detail_apiKeys_navigateToOrgAgentKeys'
                    }
                    values={{
                      a: (msg) => (
                        <ExternalAgentLink isEmbeddedInNS={isEmbeddedInNS}>
                          {msg}
                        </ExternalAgentLink>
                      )
                    }}
                  />
                </div>
              )}
            />
          </div>
        </div>

        <div className={footerCss}>
          <SampleApiRequest
            apiKey={apiKey}
            organization={organization}
            contrastURL={contrastURL}
            acceptHeader="application/json"
            authHeader={authHeader}
            onCopyAPIRequest={onCopyAPIRequest}
          />
        </div>
      </div>
    )
  }

  private copyToClipboard = (str: string, onCopyFn: () => void) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    clipboardCopy(str)
    onCopyFn()
  }
}

interface YourKeysSuperadminProps extends YourKeysParentProps {
  readonly organizations?: OrganizationProps[]
  readonly onSelectedOrg?: (orgID: string) => void
}

export interface YourKeysSuperadminState {
  readonly organizationID?: string
}

export class YourKeysSuperadmin extends React.Component<
  YourKeysSuperadminProps,
  YourKeysSuperadminState
> {
  public state: YourKeysSuperadminState = {}

  public render() {
    const { organizations, ...rest } = this.props
    const { organizationID } = this.state

    if (!organizations || organizations.length === 0) {
      return false
    }

    const selectedOrganization =
      organizations.find((org) => org.id === organizationID) || organizations[0]

    return (
      <YourKeys
        {...rest}
        role="ADMIN"
        organization={selectedOrganization}
        renderOrganizationDropdown={() => {
          return (
            organizations.length > 1 && (
              <Field
                className={itemSpaceCss}
                label={{
                  id: 'title_organization'
                }}
                description={
                  <Dropdown
                    selectedItem={selectedOrganization}
                    items={organizations}
                    itemToString={(org) => org.name}
                    itemToKey={(org) => org.id}
                    onChange={this.onSelectOrg}
                    styleOverrides={{
                      outerContainerCss: dropdownCss
                    }}
                  />
                }
                testId="organization-dropdown"
              />
            )
          )
        }}
      />
    )
  }

  private onSelectOrg = (org: OrganizationProps) => {
    const { onSelectedOrg } = this.props
    const { id } = org

    this.setState(
      {
        organizationID: id
      },
      () => {
        if (onSelectedOrg) {
          onSelectedOrg(id)
        }
      }
    )
  }
}
